import React, { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Script from "next/script";
import { RECAPTCHA_KEY } from "@gcloud-npm/utils.c2c";
import toKebabCase from "@gcloud-npm/utils.common.to-kebap-case";
import normalizeString from "@gcloud-npm/utils.metrics.normalize-string";
import {
  validateAnyPhone,
  validateEmail,
  validateRequired,
  docNumberValidation,
  validateDateBefore,
  validateDateAfter
} from "@gcloud-npm/utils.validators";
import styled from 'styled-components';
import CmsFormPage from "@/bit/components/components.cms-block.cms-form.cms-form-page";
import {
  sendToRetargeting,
  sendTaggingInfo,
  createDefaultValues,
  getSteps,
  reconstructData,
  reconstructConsents,
  getPurchaseTag
} from "./utils";
import { ALIGN_FORM_TYPES} from './enums.js';
import Text from "@/bit/components/components.text";
import StepperForm from "@/bit/components/components.stepper-form";

const CmsAddElement = dynamic(() =>
  import("@/bit/components/components.cms-block.cms-add-element")
);

const CmsEditableGroupIcons = dynamic(
  () => import("@/bit/components/components.cms-block.cms-editable-group-icons")
);

const CmsFormStyle = styled.div`
  background-color: white;
  .CmsForm {
    width: ${props => props.width ? props.width : "100%"};
    ${props => props.formAlign === 'right' ? 'margin-left: auto;': props.formAlign === 'left'? 'margin-right: auto;' : 'margin: auto;'}
    padding-top: 1rem;
    padding-bottom: 1rem;
    min-height: 25rem;
    display: flex;
    align-items: center;
    .subtitle-wrapper {
      padding-top: 1rem;
    }
    .form-section {
      width: 100%;
    }
    .wrapper-pages-add {
      width: 100%;
    }
  }
  @media (max-width: 768px) { 
    .CmsForm {
      width: 100%;
      margin: auto;
    }
  }
  .no-pointer-event {
    pointer-events: none;
  }
`


const ID_TEMPLATE_ORION = "1776265";

const CmsForm = (props) => {
  const [data, setData] = useState(
    createDefaultValues(props["cms-form-pages-1"])
  );
  const [pageIncorrects, setPageIncorrects] = useState([]);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [screen, setScreen] = useState(1);
  const [waiting, setWaiting] = useState(false);
  const { context } = useSelector((state) => state.main);
  const { idForm, idFormRetargeting, cmsHandler, blockName, alias } = props;

  const taggingInfo = {
    componentName: idForm,
    componentVariation: "all",
  };
  const steps = getSteps(props["cms-form-pages-1"]);
  const dataRef = useRef(data);
  const isSubmitted = useRef(false);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const handleHashChange = () => {
    if (isSubmitted.current) return;
    const pageIncorrect = getInvalidPages(dataRef.current);
    setPageIncorrects(pageIncorrect);
    const hash = window.location.hash.substring(1);
    const hashScreen = steps.findIndex(
      (item) => normalizeString(toKebabCase(item.label)) === hash
    );
    if (hashScreen !== -1) {
      setScreen(hashScreen + 1);
    } else {
      setScreen(1);
    }
    scrollToTop();
  };

  useEffect(() => {
    if(props.isPurchaseProcess === 'true' || props.isPurchaseProcess === true) {
      sendTaggingInfo(
        getPurchaseTag(props.product, 'resumen', props.price)
        ,
        null,
        "funnel"
      );
    } else {
      sendTaggingInfo(
        {
          funnelStepName: "inicio",
          funnel_flow_detail: idForm,
        },
        "print-success",
        "funnel"
      );
    }

    if(`${process.env.NEXT_PUBLIC_ENV_NAME}` !== "prod") {
      window.location.hash = "";
    } else {
      const persistData = JSON.parse(sessionStorage.getItem(props.idForm));
      if (persistData) {
        setData(persistData.data);
        if (persistData.screen <= props["cms-form-pages-1"].length) {
          if (
            window.location.hash.substring(1) !=
            normalizeString(toKebabCase(steps[persistData.screen - 1].label))
          ) {
            window.location.hash = `${normalizeString(toKebabCase(steps[persistData.screen - 1].label))}`;
          } else {
            setScreen(persistData.screen);
          }
        }
      } else {
        window.location.hash = "";
      }
    }
    window.addEventListener("popstate", handleHashChange);

    const existingScript = document.querySelector(
        `script[src="https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_KEY}"]`
      );
  
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = onReadyRecaptcha;
        document.head.appendChild(script);
      } else {
        if (window.grecaptcha) {
          onReadyRecaptcha();
        } else {
          existingScript.addEventListener("load", onReadyRecaptcha);
        }
      }
      
    return () => {
      window.removeEventListener("popstate", handleHashChange);
    };
  }, []);

  useEffect(() => {
    if (screen != "ok" && screen != "ko" && `${process.env.NEXT_PUBLIC_ENV_NAME}` === "prod") {
      try {
        sessionStorage.setItem(props.idForm, JSON.stringify({ data, screen }));
      } catch (error) {
        console.error("Error al guardar en sessionStorage:", error);
      }
    }
  }, [data, screen]);

  const initRef = useRef(null);

  const handleSelect = (event) => {
    const question = data[event.field];
    const validation = validateField(
      question.validation || "notBlank",
      event.label,
      question.requiredField
    );
    setData((prevData) => ({
      ...prevData,
      [event.field]: {
        ...prevData[event.field],
        value: event.label,
        valid: validation.validated,
        error: validation.errorMessage,
      },
    }));
  };

  const handleInput = (element) => {
    const question = data[element.target.name];
    const validation = validateField(
      question.validation,
      element.target.value,
      question.requiredField
    );
    setData((prevData) => ({
      ...prevData,
      [element.target.name]: {
        ...prevData[element.target.name],
        value: element.target.value,
        valid: validation.validated,
        error: validation.errorMsg,
      },
    }));
  };

  const handleCheck = (element) => {
    const question = data[element.target.name];
    const validation = validateField(
      question.validation,
      element.target.checked,
      question.requiredField
    );
    setData((prevData) => ({
      ...prevData,
      [element.target.name]: {
        ...prevData[element.target.name],
        value: element.target.checked,
        label: question.label,
        valid: validation.validated,
        error: validation.errorMsg,
      },
    }));
  };

  const handleRadio = (element) => {
    const question = data[element.target.name];
    const validation = validateField(
      question.validation,
      element.target.value,
      question.requiredField
    );
    setData((prevData) => ({
      ...prevData,
      [element.target.name]: {
        ...prevData[element.target.name],
        value: element.target.value,
        label: question.label,
        valid: validation.validated,
        error: validation.errorMsg,
      },
    }));
  };

  const handleDate = ({value, name}) => {
    debugger;
    const question = data[name];
    const validation = validateField(
      question.validation,
      value,
      question.requiredField
    );
    setData((prevData) => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value: value,
        label: question.label,
        valid: validation.validated,
        error: validation.errorMsg,
      },
    }));
  };

  const handleField = {
    handleSelect,
    handleInput,
    handleCheck,
    handleRadio,
    handleDate,
  };

  const validateField = (type, value, required) => {
    let validation;
    switch (type) {
      case "notBlank":
        if (required) {
          validation = validateRequired(value);
        } else {
          validation = { validated: true, errorMessage: "" };
        }
        break;
      case "phone":
        validation = validateAnyPhone(value, { required });
        break;
      case "email":
        validation = validateEmail(value, { required });
        break;
      case "dni":
        validation = docNumberValidation(value, {
          required,
          allowDocTypes: ["DNI"],
        });
        break;
      case "cif":
        validation = docNumberValidation(value, {
          required,
          allowDocTypes: ["CIF"],
        });
        break;
      case "dateBefore":
        validation = validateDateBefore(value, { required, isBefore: true });
        break;
      case "dateAfter":
        validation = validateDateAfter(value, { required, isBefore: false });
        break;
      default:
      
    }
    return validation;
  };

  const onReadyRecaptcha = () => {
    setRecaptchaReady(true);
  };

  const validData = (form) => {
    let isValid = true;
    const updatedForm = Object.fromEntries(
      Object.entries(form).map(([key, field]) => {
        const validate = validateField(
          field.validation,
          field.value,
          field.required
        );
        if (!validate.validated) {
          isValid = false;
        }
        return [
          key,
          { ...field, valid: validate.validated, error: validate.errorMsg },
        ];
      })
    );
    if (!isValid) {
      setData((prevData) => ({
        ...prevData,
        ...updatedForm,
      }));
    }
    return isValid;
  };

  const getInvalidPages = (data) => {
    const invalidPages = [];
    const seenPages = new Set();
    for (const field of Object.values(data)) {
      const { valid, page } = field;
      if (valid === false && !seenPages.has(page) && page !== steps.length) {
        invalidPages.push({
          page,
          label: steps[page - 1].label
        });
        seenPages.add(page);
      }
    }
    return invalidPages.sort((a, b) => a.page - b.page);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (props["cms-form-pages-1"].length !== screen) {
      const questionList = props["cms-form-pages-1"][screen - 1]["cms-ml-question-list-1"];
      debugger;
      const currentValues = {};
      Object.keys(data).forEach(key => {
        if (questionList.some(question => question.nameId === key)) {
          currentValues[key] = data[key];
        }
      });
      if (validData(currentValues)) {
        if (
          window.location.hash.substring(1) !==
          normalizeString(toKebabCase(steps[screen].label))
        ) {
          window.location.hash = `${normalizeString(toKebabCase(steps[screen].label))}`;
        } else {
          setScreen(screen + 1);
        }
      }
    } else if (validData(data)) {
      setWaiting(true);
      const dataReconstructed = reconstructData(data, props.needIdPartyHash);
      const consentReconstructed = reconstructConsents(data);
      isSubmitted.current = true;
      try {
        sendToRetargeting(context, dataReconstructed, consentReconstructed, idFormRetargeting)
          .then((value) => {
            if(props.isPurchaseProcess === 'true' || props.isPurchaseProcess === true) {
              sendTaggingInfo(
                getPurchaseTag(props.product, 'confirmacion', props.price)
                ,
                null,
                "funnel"
              );
            } else {
              sendTaggingInfo(
                {
                  funnelStepName: "fin",
                  funnel_flow_detail: idForm,
                  lead_info: "contact",
                  lead_target: "N/A",
                },
                "print-success",
                "profiling_form"
              );
            }
            setWaiting(false);
            sessionStorage.removeItem(props.idForm);
            window.removeEventListener("popstate", handleHashChange);
            setScreen("ok");

            scrollToTop();
          })
          .catch((error) => {
            if(props.isPurchaseProcess === 'true' || props.isPurchaseProcess === true) {
              sendTaggingInfo(
                {
                  component_content: "error ",
                  component_name: "proceso-compra",
                  component_product: props.product,
                  component_variation: "generico"
                }
                ,
                null,
                "cms_component"
              );
            } else {
              sendTaggingInfo(
                {
                  funnelStepName: "error",
                  funnel_flow_detail: idForm,
                  lead_info: "contact",
                  lead_target: "N/A",
                },
                "print-success",
                "profiling_form"
              );
            }
            setWaiting(false);
            sessionStorage.removeItem(props.idForm);
            window.removeEventListener("popstate", handleHashChange);
            setScreen("ko");

            scrollToTop();
          });
      } catch (error) {
        if(props.isPurchaseProcess === 'true' || props.isPurchaseProcess === true) {
          sendTaggingInfo(
            {
              component_content: "error ",
              component_name: "proceso-compra",
              component_product: props.product,
              component_variation: "generico"
            }
            ,
            null,
            "cms_component"
          );
        } else {
          sendTaggingInfo(
            {
              funnelStepName: "error",
              funnel_flow_detail: idForm,
              lead_info: "contact",
              lead_target: "N/A",
            },
            "print-success",
            "profiling_form"
          );
        }
        setWaiting(false);
        sessionStorage.removeItem(props.idForm);
        window.removeEventListener("popstate", handleHashChange);
        setScreen("ko");

        scrollToTop();
      }
    } else {
      const pageIncorrect = getInvalidPages(data);
      setPageIncorrects(pageIncorrect);
    }
    return false;
  };

  const addElementCmp = (index = "start") =>
    cmsHandler && (
      <CmsAddElement
        addProps={{
          index,
          idTemplateOrion: ID_TEMPLATE_ORION,
          itemIdDotNotation: `${blockName}.cms-form-pages-1`,
          alias,
          layout: 'vertical',
          label: 'Añadir pagina'
        }}
        cmsHandler={cmsHandler}
      />
    );

  return (
    <CmsFormStyle width={props.width} formAlign={props.formAlign} className="container">
      <div
        ref={initRef}
        className={`CmsForm  ${waiting ? "no-pointer-event" : ""}`}
      >
        <CmsEditableGroupIcons
          elementProps={{
            ...props,
            editName: "cms-block.cms-form.cms-form",
          }}
          deleteProps={{}}
          visibleTools={{
            hiring: false,
            edit: true,
            deleteItem: false,
          }}
          cmsHandler={cmsHandler}
        />
        <form
          onSubmit={handleSubmit}
          autoComplete="no"
          id="formC2C"
          name="formC2C"
          className="form-section"
        >
          {((screen !== "ok" && screen !== "ko") || cmsHandler) && (
            <>
              {props["cms-atom-text-1"] && (
                <div className="title-wrapper">
                  <Text cmsHandler={cmsHandler} {...props["cms-atom-text-1"]} />
                </div>
              )}
              {props["cms-atom-text-2"] && (
                <div className="subtitle-wrapper">
                  <Text cmsHandler={cmsHandler} {...props["cms-atom-text-2"]} />
                </div>
              )}
              {steps.length > 1 && (
                <StepperForm
                  steps={steps}
                  currentStep={screen}
                  onStepClick={({ step, index }) =>
                    (window.location.href = `#${normalizeString(toKebabCase(step.label))}`)
                  }
                />
              )}
              <div className="wrapper-pages-add">
                {addElementCmp('start')}
              </div>
              <div className="wrapper-pages">
                {props["cms-form-pages-1"].map((element, index) => (
                  <>
                    {/* Mostrar número de página solo si existe cmsHandler */}
                    {cmsHandler && (<div style={{margin: '1rem 0', borderTop: 'solid 1px black'}}>VISUALIZACION PAGINA {element.namePage || index + 1}</div>)}
                    {(screen - 1 === index || cmsHandler) && (
                      <CmsFormPage
                        parentLocation='cms-form-pages-1'
                        cmsHandler={cmsHandler}
                        blockName={blockName}
                        alias={alias}
                        {...element}
                        taggingInfo={taggingInfo}
                        handleField={handleField}
                        data={data}
                        recaptchaReady={recaptchaReady}
                        pageIncorrects={pageIncorrects}
                        waiting={waiting}
                        send={index + 1 === props["cms-form-pages-1"].length}
                        isResult={false}
                        index={index}
                        frontalListToDelete={props["cms-form-pages-1"]}
                        formAlign={props.formAlign}
                      />
                    )}
                  </>
                ))}
                </div>
              {props["cms-form-pages-1"].length > 0 && (<div className="wrapper-pages-add">
                {addElementCmp('end')}
              </div>)}
            </>
          )}

          {(screen === "ok" || cmsHandler) && (
            <>
            {cmsHandler && (<div style={{margin: '1rem 0', borderTop: 'solid 1px black'}}>Pagina OK</div>)}
            <CmsFormPage
              parentLocation="cms-form-result-page-1"
              cmsHandler={cmsHandler}
              blockName={blockName}
              alias={alias}
              {...props["cms-form-result-page-1"]}
              recaptchaReady={recaptchaReady}
              isResult={true}
              send={false}
            />
            </>
          )}
          {(screen === "ko" || cmsHandler) && (
            <>
              {cmsHandler && (<div style={{margin: '1rem 0', borderTop: 'solid 1px black'}}>Pagina KO</div>)}
              <CmsFormPage
                parentLocation="cms-form-result-page-1"
                cmsHandler={cmsHandler}
                blockName={blockName}
                alias={alias}
                {...props["cms-form-result-page-2"]}
                recaptchaReady={recaptchaReady}
                isResult={true}
                send={false}
              />
            </>
          )}
        </form>
      </div>
    </CmsFormStyle>
  );
};

CmsForm.propTypes = {
   /**
   * {
   *  "info": "Identificador del formulario",
   *  "kind": "both",
   *  "beautifulName": "Id formulario"
   * }
   */
  idForm: PropTypes.string,
   /**
   * {
   *  "info": "Id de la campaña de retargeting",
   *  "kind": "both",
   *  "beautifulName": "Id campaña retargeting"
   * }
   */
  idFormRetargeting: PropTypes.string,
   /**
   * {
   *  "info": "ancho del formulario, si lo dejas vacio sera 100%",
   *  "kind": "both",
   *  "beautifulName": "Ancho formulario"
   * }
   */
  width: PropTypes.string,
   /**
   * {
   *  "info": "alineacion del formulario",
   *  "kind": "both",
   *  "beautifulName": "Alineación formulario",
   *  "values": "./enums.js:ALIGN_FORM_TYPES"
   * }
   */
  formAlign: PropTypes.oneOf(Object.values(ALIGN_FORM_TYPES)),
  /**
   * {
   *  "info": "Hace que se envie el idPartyHash, pero ojo, que enviar información personal y idPartyHash dara error",
   *  "kind": "both",
   *  "beautifulName": "¿Necesita envio de partyHash?"
   * }
   */
  needIdPartyHash: PropTypes.bool,
  /**
   * {
   *  "info": "Lo marca como proceso de compra, esto solo afecta a tageado",
   *  "kind": "both",
   *  "beautifulName": "¿Es proceso de compra?"
   * }
   */
  isPurchaseProcess: PropTypes.bool,
  /**
   * {
   *  "info": "Nombre del producto, esto solo afecta a tageado",
   *  "kind": "both",
   *  "beautifulName": "Producto"
   * }
   */
  product: PropTypes.string,
  /**
   * {
   *  "info": "Precio del producto, esto solo afecta a tageado",
   *  "kind": "both",
   *  "beautifulName": "Precio"
   * }
   */
  price: PropTypes.string
};

CmsForm.defaultProps = {
  formAlign: ALIGN_FORM_TYPES.CENTER,
  isPurchaseProcess: false
};

export default CmsForm;
